import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import CorporateFareIcon from '@mui/icons-material/CorporateFare'

import { Box, Typography } from '@mui/material'
import {
  getGridDateOperators,
  getGridStringOperators,
  GridAggregationFunction,
  GridColDef,
  GridRenderCellParams,
  GridValueFormatter,
  GridValueGetter,
} from '@mui/x-data-grid-premium'
import 'components/DataGrid/DataGridContext.css'
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import { customSortOperator } from 'components/DataGrid/operators/customSortOperator'
import { parseNumeric } from 'components/DataGrid/operators/formatNumbers'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'

import { parseDateLocal } from 'utils/formatDayjsDateTime'
import RenderDateCell from 'components/Deals/grid/cells/RenderDateCell'
import RenderStatusCell from 'components/Deals/grid/cells/RenderStatusCell'
import AggregateCurrencyCell from 'components/MTB/grid/cells/AggregateCell'
import RenderOrganizationCell from 'components/MTB/grid/cells/RenderOrganizationCell'
import TeamDetailsCell from 'components/MTB/grid/cells/TeamDetailsCell'
import RenderServiceIcon from 'components/RenderServiceIcons/RenderServiceIcon'
import FlexTypography from 'components/ui/flex-typography'
import { FlexRow } from 'theme/components'
import { DensityStyleTypes, Product, ProductRowFields } from 'types'
import ProductChangeCell from './ProductChangeCell'

const loopServices = ['Loop Core', 'Loop CallRail Integration', 'Loop Setup', 'Loop Advanced']
const currencyValueFormatter: GridValueFormatter = ({ value }) =>
  value == null
    ? ''
    : (value as number).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })

export const safeSum: GridAggregationFunction<number, number> = {
  label: 'Safe sum',
  apply: ({ values }) => values.reduce((sum, v) => (typeof v === 'number' && !Number.isNaN(v) ? sum + v : sum), 0),
  valueFormatter: currencyValueFormatter,
}
export const renderProductColumns = (
  density: 'compact' | 'standard' | 'comfortable',
  openInfo: (product: ProductRowFields) => void,
  handleRowClick: (row: Product) => void,
  activeChangeType?: 'New Business' | 'Upsell' | 'Downsell' | 'Renewal' | 'Removed' | ''
): GridColDef[] => {
  const densityStyles: DensityStyleTypes = getDensityStyles(density)

  return [
    {
      field: 'productName',
      headerName: 'Service Name',
      width: 250,
      headerAlign: 'left',
      valueGetter: params => params,
      renderCell: params => {
        //@ts-ignore
        if (params?.rowNode?.isAutoGenerated) return null
        return (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => openInfo(params?.row)}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && openInfo(params?.row)}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              height: '100%',
              cursor: 'pointer',
              textDecoration: 'none',
              textUnderlineOffset: 2,
              textAlign: 'left',
            }}
          >
            <RenderServiceIcon
              service={params?.row?.productName ?? null}
              size={{
                width: loopServices?.includes(params?.row?.productName)
                  ? densityStyles.iconSize + 10
                  : densityStyles.iconSize + 2,
                height: densityStyles.iconSize + 2,
                fontSize: densityStyles.fontSize,
                marginLeft: 2,
              }}
            />
          </Box>
        )
      },
    },
        {
      // Service Line replaces the hidden Category / Subcategory columns. Value
      // is set by backend/scripts/setProductType.ts based on category /
      // subcategory / name conventions (see that script for the rules).
      field: 'product_type',
      headerName: 'Service Line',
      width: 130,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_value, row) => row?.product_type ?? '',
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {

      field: 'change_type',
      headerName: 'Change',
      type: 'number',
      width: 170,
      headerAlign: 'left',
      aggregable: true,
      valueGetter: (_value, row) => {
        // Server-side aggregation row carries the pre-summed delta directly
        // in `change_type` as a number — use it verbatim instead of trying to
        // re-compute from prior_gross_profit (which doesn't exist on the
        // agg row).
        if (row?.isAggregationRow) return Number(row?.change_type) || 0
        const change = row?.change_type
        if (!change) return 0
        const gp = Number(row?.grossProfit ?? 0)
        const priorGp = row?.prior_gross_profit != null ? Number(row.prior_gross_profit) : null
        if (change === 'New Business') return gp
        if (change === 'Renewal') return 0
        if (change === 'Removed') return -gp
        if ((change === 'Upsell' || change === 'Downsell') && priorGp != null) {
          return gp - priorGp
        }
        return 0
      },
      renderCell: (params: GridRenderCellParams) => (
        <ProductChangeCell
          params={params}
          densityStyles={densityStyles}
          activeChangeType={activeChangeType || undefined}
        />
      ),
    },
    {
      field: 'organizationName',
      headerName: 'Organization',
      renderHeader: () => {
        return (
          <FlexRow extraStyles={{ gap: 1, overflow: 'hidden' }}>
            <CorporateFareIcon fontSize="small" sx={{ color: 'icon.organizations' }} />
            <Typography sx={{ fontSize: 14 }}>Organizations</Typography>
          </FlexRow>
        )
      },
      width: 150,
      headerAlign: 'left',
      aggregable: false,
      renderCell: params => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {/* <Typography sx={{ fontSize: 12 }}>{params?.value}</Typography> */}
          <RenderOrganizationCell
            params={params}
            densityStyles={densityStyles}
            handleRowClick={handleRowClick}
            toggleExpanded={() => {}}
          />
        </Box>
      ),
    },
    {
      // Parent organization resolved server-side via the org's contractedBy[0]
      // → orgsByPK lookup in productsSyncService. Empty string when the org
      // has no parent (standalone / top-level org).
      field: 'parentOrganizationName',
      headerName: 'Parent Organization',
      width: 150,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_, row) => row?.parentOrganizationName ?? '',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography sx={{ fontSize: densityStyles.fontSize, color: 'text.secondary' }}>
            {params?.value || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'price',
      headerName: 'Price',
      type: 'number',
      width: 100,
      headerAlign: 'left',
      aggregable: true,
      valueGetter: (_, row) => parseNumeric(row?.price),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },

    {
      field: 'netTotal',
      headerName: 'Net Total',
      type: 'number',
      width: 100,
      headerAlign: 'left',
      aggregable: true,
      valueGetter: (_, row) => parseNumeric(row?.netTotal),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'grossProfit',
      headerName: 'Gross Profit',
      type: 'number',
      width: 110,
      headerAlign: 'left',
      aggregable: true,
      valueGetter: (_, row) => parseNumeric(row?.grossProfit),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      // Prior version's monthly gross profit, attached to the row by the
      // backend classifier. Null on rows with no prior match (New Business,
      // Removed, no-lineage standalone deals).
      field: 'prior_gross_profit',
      headerName: 'Prior GP',
      type: 'number',
      width: 110,
      headerAlign: 'left',
      aggregable: true,
      valueGetter: (_, row) =>
        row?.prior_gross_profit != null ? parseNumeric(row.prior_gross_profit) : null,
      renderCell: (params: GridRenderCellParams) =>
        params?.value == null ? null : (
          <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
        ),
    },
    {
      field: 'mediaSpend',
      headerName: 'Spend',
      type: 'number',
      width: 100,
      headerAlign: 'left',
      aggregable: true,
      valueGetter: (_, row) => parseNumeric(row?.mediaSpend),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    // {
    //   field: '_resolvedOwner ',
    //   headerName: 'Owner',
    //   width: 75,
    //   headerAlign: 'center',
    //   aggregable: false,
    //   valueGetter: (_value, row) => {
    //     return row?.owner?.name ?? ''
    //   },
    //   filterOperators: [...getGridStringOperators(), customNotContainsOperator],
    //   sortComparator: customSortOperator,
    //   renderCell: (params: GridRenderCellParams) => {
    //     if (!params?.row?._resolvedOwner) return <ChildNoValueCell densityStyles={densityStyles} />
    //     // OwnerCell expects owner at params.row.owner
    //     const _params = { ...params, row: { ...params.row, owner: params.row._resolvedOwner } }
    //     return <OwnerCell params={_params} densityStyles={densityStyles} />
    //   },
    // },
    {
      field: 'team',
      headerName: 'Team',
      width: 100,
      headerAlign: 'center',
      aggregable: false,
      valueGetter: (_, row) => {
        return row?.team?.map((user: any) => user.name).join(', ') || ''
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => {
        // TeamDetailsCell expects team_details at params.row.team_details
        const _params = { ...params, row: { ...params.row, team_details: params.row?.team || [] } }
        return <TeamDetailsCell params={_params} densityStyles={densityStyles} />
      },
    },
    {
      field: 'dealName',
      headerName: 'Deal',
      renderHeader: params => {
        return (
          <FlexRow extraStyles={{ gap: 1, overflow: 'hidden' }}>
            <AttachMoneyIcon fontSize="small" sx={{ color: 'icon.deals' }} />
            <Typography sx={{ fontSize: 14 }}>Deal</Typography>
          </FlexRow>
        )
      },
      width: 225,
      headerAlign: 'left',
      aggregable: false,
      renderCell: params => {
        const deal = params?.row?._resolvedDeal
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: '100%',
              cursor: deal ? 'pointer' : 'default',
            }}
            onClick={() => {
              if (deal && params?.row?.PK) {
                handleRowClick(params?.row)
              }
            }}
          >
            <FlexTypography sx={{ fontSize: densityStyles.fontSize }}>{params?.value}</FlexTypography>
          </Box>
        )
      },
    },
    {
      field: 'dealStatus',
      headerName: 'Deal Status',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'dealType',
      headerName: 'Deal Type',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'dealStage',
      headerName: 'Deal Stage',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'contractId',
      headerName: 'Contract ID',
      width: 140,
      headerAlign: 'left',
      aggregable: false,
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <FlexTypography sx={{ fontSize: densityStyles.fontSize }}>{params?.value}</FlexTypography>
        </Box>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 85,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_, row) => row?.category || row?.category_name || '',
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'subcategory',
      headerName: 'Subcategory',
      width: 85,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (params: GridValueGetter) => params,
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'status',
      headerName: 'Status',
      aggregable: false,
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (params: GridValueGetter) => params,
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      type: 'date',
      aggregable: false,
      width: 100,
      headerAlign: 'left',
      filterOperators: getGridDateOperators(),
      sortComparator: customSortOperator,
      // parseDateLocal — a YYYY-MM-DD string is treated as local-midnight of
      // that calendar day, so "2026-05-01" renders as 05/01/2026 in every
      // timezone instead of slipping to 04/30 in zones west of UTC.
      valueGetter: params => parseDateLocal(params),
      renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      type: 'date',
      aggregable: false,
      width: 100,
      headerAlign: 'left',
      filterOperators: getGridDateOperators(),
      sortComparator: customSortOperator,
      valueGetter: params => parseDateLocal(params),
      renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
    },
  ]
}
